import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import {
  Loader2,
  Dumbbell,
  ChevronRight,
  Check,
  Clock,
  ArrowLeft,
  Target,
  TrendingUp,
  History
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MyTraining() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // list, workout, history
  const [workouts, setWorkouts] = useState([]);
  const [history, setHistory] = useState([]);
  
  // Active workout state
  const [activeAssignment, setActiveAssignment] = useState(null);
  const [activeWorkout, setActiveWorkout] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [loggedSets, setLoggedSets] = useState([]);
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [currentSetNumber, setCurrentSetNumber] = useState(1);
  const [inputWeight, setInputWeight] = useState('');
  const [inputReps, setInputReps] = useState('');
  const [logging, setLogging] = useState(false);
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    fetchWorkouts();
    fetchHistory();
  }, [user, authLoading, navigate]);

  const fetchWorkouts = async () => {
    try {
      const response = await axios.get(`${API_URL}/my-training/workouts`);
      setWorkouts(response.data.workouts || []);
    } catch (error) {
      console.error('Error fetching workouts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API_URL}/my-training/history`);
      setHistory(response.data.sessions || []);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const openWorkout = async (assignment) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/my-training/workout/${assignment.id}`);
      setActiveAssignment(response.data.assignment);
      setActiveWorkout(response.data.workout);
      setView('workout');
      setCurrentExerciseIndex(0);
      setCurrentSetNumber(1);
      setLoggedSets([]);
      
      // Pre-fill from previous performance if available
      const firstExercise = response.data.workout?.exercises?.[0];
      if (firstExercise?.previous?.length > 0) {
        const lastSet = firstExercise.previous[0];
        setInputWeight(lastSet.weight?.toString() || '');
        setInputReps(lastSet.reps?.toString() || '');
      } else {
        setInputWeight(firstExercise?.target_weight?.toString() || '');
        setInputReps(firstExercise?.target_reps?.toString() || '');
      }
    } catch (error) {
      toast.error('Failed to load workout');
    } finally {
      setLoading(false);
    }
  };

  const startSession = async () => {
    try {
      const response = await axios.post(`${API_URL}/my-training/workout/${activeAssignment.id}/start`);
      setSessionId(response.data.session_id);
      toast.success('Workout started! Let\'s go! 💪');
    } catch (error) {
      toast.error('Failed to start workout');
    }
  };

  const logSet = async () => {
    if (!sessionId) {
      await startSession();
      return;
    }
    
    if (!inputWeight || !inputReps) {
      toast.error('Please enter weight and reps');
      return;
    }

    setLogging(true);
    try {
      await axios.post(`${API_URL}/my-training/session/${sessionId}/log`, {
        exercise_index: currentExerciseIndex,
        set_number: currentSetNumber,
        weight: parseFloat(inputWeight),
        reps: parseInt(inputReps)
      });

      const currentExercise = activeWorkout.exercises[currentExerciseIndex];
      const newSet = {
        exercise_index: currentExerciseIndex,
        exercise_name: currentExercise.name,
        set_number: currentSetNumber,
        weight: parseFloat(inputWeight),
        reps: parseInt(inputReps)
      };
      setLoggedSets([...loggedSets, newSet]);

      // Move to next set or exercise
      if (currentSetNumber < currentExercise.sets) {
        setCurrentSetNumber(currentSetNumber + 1);
      } else if (currentExerciseIndex < activeWorkout.exercises.length - 1) {
        const nextExercise = activeWorkout.exercises[currentExerciseIndex + 1];
        setCurrentExerciseIndex(currentExerciseIndex + 1);
        setCurrentSetNumber(1);
        
        // Pre-fill from previous performance
        if (nextExercise?.previous?.length > 0) {
          const lastSet = nextExercise.previous[0];
          setInputWeight(lastSet.weight?.toString() || '');
          setInputReps(lastSet.reps?.toString() || '');
        } else {
          setInputWeight(nextExercise?.target_weight?.toString() || '');
          setInputReps(nextExercise?.target_reps?.toString() || '');
        }
      }

      toast.success('Set logged! ✓');
    } catch (error) {
      toast.error('Failed to log set');
    } finally {
      setLogging(false);
    }
  };

  const completeWorkout = async () => {
    if (!sessionId) {
      toast.error('Please log at least one set first');
      return;
    }

    setCompleting(true);
    try {
      await axios.post(`${API_URL}/my-training/session/${sessionId}/complete`);
      toast.success('Workout completed! Great job! 🎉');
      setView('list');
      setActiveAssignment(null);
      setActiveWorkout(null);
      setSessionId(null);
      fetchWorkouts();
      fetchHistory();
    } catch (error) {
      toast.error('Failed to complete workout');
    } finally {
      setCompleting(false);
    }
  };

  const goBack = () => {
    if (view === 'workout' || view === 'history') {
      setView('list');
      setActiveAssignment(null);
      setActiveWorkout(null);
      setSessionId(null);
    } else {
      navigate(-1);
    }
  };

  const currentExercise = activeWorkout?.exercises?.[currentExerciseIndex];
  const totalSetsInWorkout = activeWorkout?.exercises?.reduce((sum, ex) => sum + ex.sets, 0) || 0;
  const isLastSet = currentExerciseIndex === (activeWorkout?.exercises?.length - 1) && 
                    currentSetNumber === currentExercise?.sets;

  if (loading && view === 'list') {
    return (
      <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0B0B]">
      <Header />
      
      <main className="container mx-auto px-4 py-6 max-w-lg">
        {/* Header with back button */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={goBack}
            className="text-gray-400 hover:text-white"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-2xl font-bold text-white">
            {view === 'list' && 'My Training'}
            {view === 'workout' && (activeWorkout?.name || 'Workout')}
            {view === 'history' && 'History'}
          </h1>
        </div>

        {/* List View - Assigned Workouts */}
        {view === 'list' && (
          <div className="space-y-4">
            {/* Tab buttons */}
            <div className="flex gap-2 mb-6">
              <Button
                variant="outline"
                className="flex-1 bg-primary text-white border-primary"
                data-testid="tab-workouts"
              >
                <Dumbbell className="w-4 h-4 mr-2" />
                Workouts
              </Button>
              <Button
                variant="outline"
                onClick={() => setView('history')}
                className="flex-1 border-white/10 text-gray-400 hover:text-white"
                data-testid="tab-history"
              >
                <History className="w-4 h-4 mr-2" />
                History
              </Button>
            </div>

            {workouts.length === 0 ? (
              <div className="text-center py-12">
                <Dumbbell className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-white mb-2">No Workouts Yet</h2>
                <p className="text-gray-400">Your coach will assign workouts for you here.</p>
              </div>
            ) : (
              workouts.map((workout) => (
                <div
                  key={workout.id}
                  onClick={() => openWorkout(workout)}
                  className="bg-[#141414] rounded-2xl p-4 border border-white/5 hover:border-primary/50 cursor-pointer transition-all"
                  data-testid={`workout-card-${workout.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-white">{workout.workout?.name || workout.workout_name}</h3>
                      <p className="text-sm text-gray-400">
                        {workout.workout?.exercises?.length || 0} exercises • From {workout.trainer_name}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Assigned {new Date(workout.assigned_at).toLocaleDateString()}
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* History View */}
        {view === 'history' && (
          <div className="space-y-4">
            {/* Tab buttons */}
            <div className="flex gap-2 mb-6">
              <Button
                variant="outline"
                onClick={() => setView('list')}
                className="flex-1 border-white/10 text-gray-400 hover:text-white"
                data-testid="tab-workouts-from-history"
              >
                <Dumbbell className="w-4 h-4 mr-2" />
                Workouts
              </Button>
              <Button
                variant="outline"
                className="flex-1 bg-primary text-white border-primary"
                data-testid="tab-history-active"
              >
                <History className="w-4 h-4 mr-2" />
                History
              </Button>
            </div>

            {history.length === 0 ? (
              <div className="text-center py-12">
                <History className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-white mb-2">No History Yet</h2>
                <p className="text-gray-400">Complete a workout to see it here.</p>
              </div>
            ) : (
              history.map((session) => (
                <div
                  key={session.id}
                  className="bg-[#141414] rounded-2xl p-4 border border-white/5"
                  data-testid={`history-card-${session.id}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold text-white">{session.workout_name}</h3>
                    <Badge className="bg-green-500/20 text-green-400">
                      <Check className="w-3 h-3 mr-1" />
                      Completed
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-400 mb-2">
                    {new Date(session.completed_at).toLocaleDateString()} at {new Date(session.completed_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                  </p>
                  <div className="text-sm text-gray-500">
                    {session.sets?.length || 0} sets logged
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Active Workout View */}
        {view === 'workout' && activeWorkout && (
          <div className="space-y-4">
            {/* Progress */}
            <div className="bg-[#141414] rounded-2xl p-4 border border-white/5">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-400">Progress</span>
                <span className="text-sm text-primary font-medium">
                  {loggedSets.length} / {totalSetsInWorkout} sets
                </span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all"
                  style={{ width: `${(loggedSets.length / totalSetsInWorkout) * 100}%` }}
                />
              </div>
            </div>

            {/* Current Exercise */}
            {currentExercise && (
              <div className="bg-[#141414] rounded-2xl p-6 border border-primary/30">
                <div className="text-center mb-6">
                  <Badge className="bg-primary/20 text-primary mb-3">
                    Exercise {currentExerciseIndex + 1} of {activeWorkout.exercises.length}
                  </Badge>
                  <h2 className="text-2xl font-bold text-white mb-2">{currentExercise.name}</h2>
                  <p className="text-gray-400">
                    Set {currentSetNumber} of {currentExercise.sets}
                  </p>
                </div>

                {/* Target */}
                <div className="bg-[#0B0B0B] rounded-xl p-4 mb-4">
                  <div className="flex items-center gap-2 text-gray-400 mb-2">
                    <Target className="w-4 h-4" />
                    <span className="text-sm">Target</span>
                  </div>
                  <p className="text-white">
                    {currentExercise.target_reps} reps
                    {currentExercise.target_weight && ` @ ${currentExercise.target_weight}kg`}
                  </p>
                  {currentExercise.notes && (
                    <p className="text-sm text-gray-500 mt-1">{currentExercise.notes}</p>
                  )}
                </div>

                {/* Previous Performance */}
                {currentExercise.previous?.length > 0 && (
                  <div className="bg-[#0B0B0B] rounded-xl p-4 mb-4">
                    <div className="flex items-center gap-2 text-gray-400 mb-2">
                      <TrendingUp className="w-4 h-4" />
                      <span className="text-sm">Last time</span>
                    </div>
                    <p className="text-white">
                      {currentExercise.previous[0].reps} reps @ {currentExercise.previous[0].weight}kg
                    </p>
                  </div>
                )}

                {/* Input */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="text-sm text-gray-400 block mb-1">Weight (kg)</label>
                    <Input
                      type="number"
                      value={inputWeight}
                      onChange={(e) => setInputWeight(e.target.value)}
                      className="bg-[#0B0B0B] border-white/10 text-white text-center text-xl h-14"
                      placeholder="0"
                      data-testid="input-weight"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 block mb-1">Reps</label>
                    <Input
                      type="number"
                      value={inputReps}
                      onChange={(e) => setInputReps(e.target.value)}
                      className="bg-[#0B0B0B] border-white/10 text-white text-center text-xl h-14"
                      placeholder="0"
                      data-testid="input-reps"
                    />
                  </div>
                </div>

                {/* Log Button */}
                <Button
                  onClick={logSet}
                  disabled={logging}
                  className="w-full h-14 text-lg bg-primary hover:bg-primary/90"
                  data-testid="log-set-button"
                >
                  {logging ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : !sessionId ? (
                    'Start & Log Set'
                  ) : isLastSet ? (
                    'Log Final Set'
                  ) : (
                    'Log Set'
                  )}
                </Button>
              </div>
            )}

            {/* Logged Sets Summary */}
            {loggedSets.length > 0 && (
              <div className="bg-[#141414] rounded-2xl p-4 border border-white/5">
                <h3 className="font-semibold text-white mb-3">Logged Sets</h3>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {loggedSets.map((set, index) => (
                    <div key={index} className="flex justify-between items-center text-sm">
                      <span className="text-gray-400">{set.exercise_name} - Set {set.set_number}</span>
                      <span className="text-white">{set.weight}kg × {set.reps}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Complete Button */}
            {loggedSets.length > 0 && (
              <Button
                onClick={completeWorkout}
                disabled={completing}
                variant="outline"
                className="w-full h-12 border-green-500 text-green-400 hover:bg-green-500/10"
                data-testid="complete-workout-button"
              >
                {completing ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Check className="w-5 h-5 mr-2" />
                    Complete Workout
                  </>
                )}
              </Button>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
